import { useMemo, type ReactNode } from 'react'

import type { ApiError } from './api-error'
import { AuthContext, type AuthContextValue, type User } from './auth-context'
import { getRuntimeConfig } from './config'

/**
 * Anmeldung ohne Entra: liefert einen erfundenen Nutzer, macht keine
 * Netzwerkaufrufe und braucht keine Konfiguration. Das ist der Grund, warum ein
 * neuer Modulentwickler in wenigen Minuten loslegen kann.
 *
 * Nutzer und Rollen lassen sich über `.env.local` ändern, um Rollen-Guards zu
 * testen:
 *
 * ```
 * VITE_MOCK_USER_NAME=Anna Beispiel
 * VITE_MOCK_USER_ROLES=wartung.nutzer
 * ```
 */
export function MockAuthProvider({
  children,
  onApiAuthError,
}: {
  children: ReactNode
  onApiAuthError?: (error: ApiError, reauthenticate: () => void) => void
}) {
  const value = useMemo<AuthContextValue>(() => {
    const config = getRuntimeConfig()
    const user: User = {
      id: config.mockUser.id,
      name: config.mockUser.name,
      email: config.mockUser.email,
      roles: config.mockUser.roles,
    }

    return {
      user,
      isAuthenticated: true,
      isLoading: false,
      error: null,
      mode: 'mock',
      login: () => {},
      retryAuthentication: () => {},
      logout: () => {
        // Im Mock-Modus gibt es nichts abzumelden. Der Hinweis verhindert, dass
        // jemand denkt, der Knopf sei kaputt.
        console.info(
          '[berky] Abmelden ist im Mock-Modus wirkungslos. Für echte Anmeldung VITE_AUTH_MODE=entra setzen.',
        )
      },
      getAccessToken: async () => null,
      reportApiAuthError: (error) => onApiAuthError?.(error, () => {}),
    }
  }, [onApiAuthError])

  return <AuthContext value={value}>{children}</AuthContext>
}
